// dashboard/src/pages/apps-setup/RandomSeedPanel.tsx
// The app's random seed (api lib/random-seed.ts): sent to CI as
// app.random_seed, so the base repo randomizes each white app its own way and
// the same seed reproduces the same app. Drawn at the first build and kept;
// set one by hand to rebuild what another build had, or draw a new one. A
// change applies from the next build; builds already made keep their seed.
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import { setAppRandomSeed, RANDOM_SEED_MAX, type AppRandomSeedStatus } from '../../api/app-builds';

const SOURCE_LABELS: Record<string, string> = {
  random: 'drawn at random',
  manual: 'set by hand',
};

function fmtDay(s: number): string {
  return new Date(s * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function RandomSeedPanel({ appId, status }: { appId: string; status: AppRandomSeedStatus | undefined }) {
  const qc = useQueryClient();
  const current = status?.seed ?? null;
  const [typed, setTyped] = useState('');
  useEffect(() => { setTyped(current ? String(current) : ''); }, [current]);

  const save = useMutation({
    mutationFn: (seed: number | null) => setAppRandomSeed(appId, seed),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['build-config', appId] }),
  });

  const parsed = /^\d+$/.test(typed.trim()) ? Number(typed.trim()) : NaN;
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= RANDOM_SEED_MAX;

  return (
    <section className="bg-surface border border-border rounded-lg p-4 space-y-3">
      <h3 className="font-medium text-content">Random seed</h3>
      {current ? (
        <div className="space-y-1">
          <code className="text-content text-sm">{current}</code>
          <p className="text-content-subtle text-xs">
            {status?.source && <>{SOURCE_LABELS[status.source] ?? status.source}{status.assigned_at ? ` on ${fmtDay(status.assigned_at)}` : ''} · </>}
            {status?.builds_with_it ?? 0} build{status?.builds_with_it === 1 ? '' : 's'} with it
          </p>
        </div>
      ) : (
        <p className="text-content-subtle text-sm">Not assigned yet — one is drawn at random at the first build.</p>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-content-subtle text-xs">Set seed</span>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            inputMode="numeric"
            placeholder={`1 – ${RANDOM_SEED_MAX}`}
            className="w-40 px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content"
          />
        </label>
        <button
          type="button"
          disabled={!valid || parsed === current || save.isPending}
          onClick={() => save.mutate(parsed)}
          className="px-3 py-1.5 text-sm rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-40"
        >
          Save
        </button>
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => {
            if (current && !confirm('Draw a new seed? The next build comes out different from the ones before.')) return;
            save.mutate(null);
          }}
          className="px-3 py-1.5 text-sm rounded-md border border-border text-content hover:bg-surface-muted disabled:opacity-40"
        >
          Draw new
        </button>
      </div>
      <p className="text-xs text-content-subtle">
        Sent to CI as <code>app.random_seed</code>. Applies from the next build; builds already made keep their seed —
        copy one from the builds list below to rebuild it the same way.
      </p>
      {typed.trim() && !valid && <p className="text-xs text-red-500">A whole number from 1 to {RANDOM_SEED_MAX}.</p>}
      {save.error && <p className="text-red-500 text-sm">{(save.error as ApiError).message}</p>}
    </section>
  );
}
